import React, { FunctionComponent, useEffect, useRef, useState } from "react";

import Search from "@ui/components/cta/Search";
import OutboundLinkIcon from "@ui/components/icon/OutboundLinkIcon";
import Drawer from "@ui/components/navigation/Drawer/Drawer";
import NavigationDrawerIdentityAuth from "@ui/components/navigation/Navigation/components/NavigationDrawer/components/NavigationDrawerIdentityAuth";
import SocialLinks from "@ui/components/navigation/SocialLinks";
import { useNavigationContext } from "@ui/context/navigation-context";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { type NavItem } from "@voxmedia/duet-context/context/global-data-context";
import { useLoopNavContext } from "@voxmedia/duet-context/context/loop-nav-context";

import AccordionMenu from "./components/AccordionMenu";
import CloseButton from "./components/CloseButton";
import * as styles from "./NavigationDrawer.css";

interface Props {
  items: Array<NavItem>;
  open: boolean;
  onClose: () => void;
  shortcutBar?: React.ReactElement;
  endSection?: React.ReactElement;
  background?: React.ReactElement;
  showAuth?: boolean;
  /** Focus on search when opening the drawer*/
  initialSearchFocus?: boolean;
  /** Render statically for storybook and chromatix */
  storybookStatic?: boolean;

  /** Randomize search placeholder */
  randomizeSearchPlaceholder?: string[];
}

const NavigationDrawer: FunctionComponent<Props> = ({
  items,
  onClose,
  open,
  shortcutBar,
  endSection,
  background,
  showAuth,
  initialSearchFocus,
  storybookStatic,
  randomizeSearchPlaceholder = [],
}) => {
  const { drawerId } = useNavigationContext();
  const analytics = useAnalytics();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchPlaceholder, setSearchPlaceholder] = useState("Search");

  const { isDrawerOpen: isLoopNavDrawerOpen, closeDrawer: closeLoopNavDrawer } =
    useLoopNavContext();

  // Close loopNav if it is open.
  useEffect(() => {
    if (open && isLoopNavDrawerOpen) {
      closeLoopNavDrawer();
    }
  }, [closeLoopNavDrawer, open, isLoopNavDrawerOpen]);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = searchInputRef.current?.value || null;
    if (query) {
      window.location.href = `/search?q=${query}`;
    } else {
      window.location.href = "/search";
    }
  };

  useEffect(() => {
    if (open && randomizeSearchPlaceholder.length) {
      const idx = Math.floor(Math.random() * randomizeSearchPlaceholder.length);
      setSearchPlaceholder(randomizeSearchPlaceholder[idx]!);
    }
  }, [open, randomizeSearchPlaceholder]);

  return (
    <Drawer
      drawerClassName={styles.drawer}
      drawerId={drawerId}
      onClose={onClose}
      open={open}
      storybookStatic={storybookStatic}
      initialFocusElement={initialSearchFocus && searchInputRef.current}
      aria-label="Navigation Drawer"
    >
      <div className={styles.drawerFlexContainer}>
        <div>
          {background && background}
          <div>
            <div className={styles.topContainer}>
              {showAuth ? <NavigationDrawerIdentityAuth /> : <div />}
              <CloseButton onClick={onClose} aria-controls={drawerId} />
            </div>
            <div>
              <form onSubmit={handleSearch} aria-label="Search Form">
                <Search
                  inputClassName={styles.search}
                  iconClassName={styles.searchIcon}
                  ref={searchInputRef}
                  placeholder={searchPlaceholder}
                />
              </form>
            </div>
            {shortcutBar}
            <nav
              className={styles.accordionContainer}
              aria-label="Drawer Navigation"
            >
              <AccordionMenu>
                {items.map((item, index) => {
                  if (!item.subItems || item.subItems.length === 0) {
                    return (
                      <AccordionMenu.Item
                        key={index}
                        href={item.allLink?.url || item.url}
                        target={item.allLink?.target || item.target}
                        rel="noreferrer"
                        special={item.special}
                        onClick={() =>
                          (item.allLink?.url || item.url) &&
                          analytics?.emit({
                            event: "navigation_click",
                            navigation_click: {
                              name: item.allLink?.url || item.url,
                            },
                          })
                        }
                      >
                        {item.title}
                        {(item.target === "_blank" ||
                          item.allLink?.target === "_blank") && (
                          <OutboundLinkIcon
                            className={styles.outboundLinkIcon}
                          />
                        )}
                      </AccordionMenu.Item>
                    );
                  }
                  return (
                    <AccordionMenu.Group
                      key={index}
                      title={item.title}
                      special={item.special}
                      defaultOpen={item.defaultOpen}
                    >
                      {item.allLink && (
                        <AccordionMenu.GroupItem
                          href={item.allLink.url}
                          target={item.allLink.target}
                          onClick={() =>
                            item.allLink?.title &&
                            analytics?.emit({
                              event: "navigation_click",
                              navigation_click: {
                                name: item.allLink.title,
                              },
                            })
                          }
                        >
                          {item.allLink.title}
                          {item.allLink.target === "_blank" && (
                            <OutboundLinkIcon
                              className={styles.outboundLinkIcon}
                            />
                          )}
                        </AccordionMenu.GroupItem>
                      )}
                      {item.subItems.map((subItem, subItemIndex) => (
                        <AccordionMenu.GroupItem
                          key={subItemIndex}
                          href={subItem.url}
                          target={subItem.target}
                          onClick={() =>
                            analytics?.emit({
                              event: "navigation_click",
                              navigation_click: {
                                name: subItem.title,
                              },
                            })
                          }
                        >
                          {subItem.title}
                          {subItem.target === "_blank" && (
                            <OutboundLinkIcon
                              className={styles.outboundLinkIcon}
                            />
                          )}
                        </AccordionMenu.GroupItem>
                      ))}
                    </AccordionMenu.Group>
                  );
                })}
              </AccordionMenu>
            </nav>
          </div>
          <div>{endSection}</div>
        </div>
        <div className={styles.flexSpacer} />
        <div className={styles.drawerFooter}>
          <SocialLinks />
        </div>
      </div>
    </Drawer>
  );
};

export default NavigationDrawer;
